/**
 * MAARAN AI API Client
 *
 * This module provides API functions to interact with the MAARAN AI backend.
 * The MAARAN API follows a session-based chat model with HITL (Human-in-the-Loop)
 * confirmation workflow support.
 *
 * Authentication:
 * - Uses EasyGTM token for API calls
 * - Passes user_id, brand_id, organization_id for context
 */

import { generateMaaranToken, getMaaranUserContext } from "@/utils/easygtm";

const BASE_URL =
  process.env.NEXT_PUBLIC_MAARAN_API_URL || "http://localhost:8001";
const API_PREFIX = "/api/v1";

/**
 * Get user context (synchronous)
 */
const getUserContext = () => {
  return getMaaranUserContext();
};

/**
 * Clear user context cache (call on login/logout)
 * Now just a no-op since we read directly from cookies
 */
export const clearUserContextCache = () => {
  // No-op - context is read fresh from cookies each time
};

/**
 * Process a binary file via the backend.
 * - PDF/DOCX → extracts text content
 * - Images → uploads to EasyGTM assets, returns URL
 * @param {File} file - File object
 * @param {string} [brandId] - Brand ID for asset uploads
 * @returns {Promise<{name, type, size, content, url, asset_id}>}
 */
export const extractFileText = async (file, brandId, campaignId) => {
  const userContext = getUserContext();
  const formData = new FormData();
  formData.append("file", file);
  if (brandId || userContext.brandId) {
    formData.append("brand_id", brandId || userContext.brandId);
  }
  if (campaignId) {
    formData.append("campaign_id", campaignId);
  }

  const headers = {};
  if (userContext.token) {
    headers["Authorization"] = `Bearer ${userContext.token}`;
  } else {
    console.warn("[extractFileText] No auth token available — EasyGTM upload will fail");
  }

  console.log("[extractFileText] Uploading:", file.name, "hasToken:", !!userContext.token, "brandId:", brandId || userContext.brandId);

  const response = await fetch(`${BASE_URL}${API_PREFIX}/extract-text`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    let detail = "Failed to extract text";
    try {
      const body = await response.json();
      detail = body?.detail || detail;
    } catch {
      console.log('error');
    }
    throw new Error(detail);
  }

  return await response.json();
};

const API_TIMEOUT_MS = 60000; // 60 seconds

const apiRequest = async (url, options = {}) => {
  const userContext = getUserContext();

  const defaultHeaders = {
    "Content-Type": "application/json",
  };

  // Add auth token if available from EasyGTM
  if (userContext.token) {
    defaultHeaders["Authorization"] = `Bearer ${userContext.token}`;
  }

  // Add timeout via AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout || API_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      const timeoutError = new Error("Request timed out. Please try again.");
      timeoutError.status = 408;
      throw timeoutError;
    }
    const networkError = new Error("Unable to connect to Maaran. Check your internet connection.");
    networkError.status = 0;
    throw networkError;
  } finally {
    clearTimeout(timeoutId);
  }

  let body;
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  // Handle 202 Accepted (HITL confirmation pending)
  if (response.status === 202) {
    return {
      ...body,
      _httpStatus: 202,
    };
  }

  if (!response.ok) {
    const error = new Error(body?.detail || body?.message || "Network Error");
    error.status = response.status;
    error.body = body;
    if(response?.status == 401) {
      const token = await generateMaaranToken();
      if(token)  return await apiRequest(url, options);
    }
    throw error;
  }

  return body;
};

// =============================================================================
// Session Management
// =============================================================================

export const createSession = async ({
  userId,
  title,
  metadata,
  brandId,
  organizationId,
  mode,
} = {}) => {
  const userContext = getUserContext();

  const sessionMetadata = { ...(metadata || {}) };
  const resolvedBrandId =
    brandId ?? sessionMetadata.brand_id ?? userContext.brandId;
  const resolvedOrganizationId =
    organizationId ??
    sessionMetadata.organization_id ??
    userContext.organizationId;
  if (resolvedBrandId) {
    sessionMetadata.brand_id = resolvedBrandId;
  }
  if (resolvedOrganizationId) {
    sessionMetadata.organization_id = resolvedOrganizationId;
  }

  const url = `${BASE_URL}${API_PREFIX}/sessions`;
  const payload = {
    user_id: userId || userContext.userId,
    title: title || "New Conversation",
    metadata: sessionMetadata,
  };
  if (mode) {
    payload.mode = mode;
  }
  const options = {
    method: "POST",
    body: JSON.stringify(payload),
  };

  return await apiRequest(url, options);
};

export const getSession = async (sessionId) => {
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}`;
  return await apiRequest(url, { method: "GET" });
};

export const listSessions = async ({
  page = 1,
  pageSize = 20,
  isActive,
  brandId,
} = {}) => {
  const userContext = getUserContext();
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (isActive !== undefined) {
    params.append("is_active", isActive.toString());
  }
  const resolvedBrandId = brandId ?? userContext.brandId;
  if (resolvedBrandId) {
    params.append("brand_id", resolvedBrandId.toString());
  }

  const url = `${BASE_URL}${API_PREFIX}/sessions?${params}`;
  return await apiRequest(url, { method: "GET" });
};

export const closeSession = async (sessionId) => {
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/close`;
  return await apiRequest(url, { method: "POST" });
};

export const deleteSession = async (sessionId) => {
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}`;
  return await apiRequest(url, { method: "DELETE" });
};

// =============================================================================
// Workflow API
// =============================================================================

export const listWorkflows = async () => {
  const url = `${BASE_URL}${API_PREFIX}/sessions/workflows/available`;
  return await apiRequest(url, { method: "GET" });
};

export const getWorkflowState = async (sessionId) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow`;
  return await apiRequest(url, { method: "GET" });
};

export const startWorkflow = async (sessionId, workflowId) => {
  if (!sessionId || !workflowId) {
    throw new Error("sessionId and workflowId are required");
  }
  const params = new URLSearchParams({ workflow_id: workflowId });
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/start?${params}`;
  return await apiRequest(url, { method: "POST" });
};

export const advanceWorkflowStep = async (
  sessionId,
  stepData = {},
  options = {},
) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }
  const params = new URLSearchParams();
  if (options?.stepId) {
    params.set("step_id", options.stepId);
  }
  if (options?.source) {
    params.set("source", options.source);
  }
  const query = params.toString();
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/advance${query ? `?${query}` : ""}`;
  return await apiRequest(url, {
    method: "POST",
    body: JSON.stringify(stepData),
  });
};

export const getWorkflowStepForm = async (sessionId, stepId) => {
  if (!sessionId || !stepId) {
    throw new Error("sessionId and stepId are required");
  }
  const params = new URLSearchParams({ step_id: stepId });
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/step?${params}`;
  return await apiRequest(url, { method: "GET" });
};

export const pauseWorkflow = async (sessionId) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/pause`;
  return await apiRequest(url, { method: "POST" });
};

export const resumeWorkflow = async (sessionId) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/resume`;
  return await apiRequest(url, { method: "POST" });
};

export const cancelWorkflow = async (sessionId) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/cancel`;
  return await apiRequest(url, { method: "POST" });
};

export const generateStepOutput = async (sessionId, stepId, options = {}) => {
  if (!sessionId || !stepId) {
    throw new Error("sessionId and stepId are required");
  }
  const params = new URLSearchParams({ step_id: stepId });
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/step/generate?${params}`;
  const payload = { existing_notes: options.existingNotes || "" };
  if (options.existingOutput !== undefined) {
    payload.existing_output = options.existingOutput;
  }
  if (options.refinementPrompt !== undefined) {
    payload.refinement_prompt = options.refinementPrompt;
  }
  if (options.toolDecision !== undefined) {
    payload.tool_decision = options.toolDecision;
  }
  return await apiRequest(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const executeStepTool = async (sessionId, { toolName, arguments: args, stepId }) => {
  if (!sessionId || !toolName) {
    throw new Error("sessionId and toolName are required");
  }
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/workflow/step/execute-tool`;
  return await apiRequest(url, {
    method: "POST",
    body: JSON.stringify({
      tool_name: toolName,
      arguments: args || {},
      step_id: stepId,
    }),
  });
};

export const setSessionMode = async (sessionId, mode) => {
  if (!sessionId || !mode) {
    throw new Error("sessionId and mode are required");
  }
  const params = new URLSearchParams({ mode });
  const url = `${BASE_URL}${API_PREFIX}/sessions/${sessionId}/mode?${params}`;
  return await apiRequest(url, { method: "POST" });
};

export const updateStepForAI = async (taskId, payload) => {
  const url = `${BASE_URL}${API_PREFIX}/tasks/${taskId}`;
  return await apiRequest(url, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
};

// =============================================================================
// Chat API
// =============================================================================

/**
 * Send a chat message to MAARAN AI
 *
 * @param {Object} params - Chat request parameters
 * @param {string} params.sessionId - Session UUID (required)
 * @param {string} [params.message] - User message (required unless responding to confirmation)
 * @param {string} [params.userId] - Optional user ID for context
 * @param {string} [params.brandId] - Optional brand ID for context
 * @param {string} [params.confirmationId] - Confirmation ID (for HITL response)
 * @param {string} [params.confirmationResponse] - "approved" or "rejected"
 * @param {Object} [params.metadata] - Optional metadata
 * @returns {Promise<Object>} Chat response with status, blocks, confirmation, etc.
 *
 * @example
 * // Send a regular message
 * const response = await sendChatMessage({
 *   sessionId: "550e8400-e29b-41d4-a716-446655440000",
 *   message: "Write a blog post about AI"
 * });
 *
 * @example
 * // Respond to HITL confirmation
 * const response = await sendChatMessage({
 *   sessionId: "550e8400-e29b-41d4-a716-446655440000",
 *   confirmationId: "660e8400-e29b-41d4-a716-446655440001",
 *   confirmationResponse: "approved"
 * });
 */
export const sendChatMessage = async ({
  sessionId,
  message,
  userId,
  brandId,
  confirmationId,
  confirmationResponse,
  metadata,
  mode,
  attachments,
}) => {
  if (!sessionId) {
    throw new Error("sessionId is required");
  }

  const userContext = getUserContext();

  const url = `${BASE_URL}${API_PREFIX}/chat`;
  const payload = {
    session_id: sessionId,
  };

  // Add message if not a confirmation response
  if (message) {
    payload.message = message;
  }

  // Add user context (from params or auto-filled from EasyGTM context)
  payload.user_id = userId || userContext.userId;
  payload.brand_id = brandId || userContext.brandId;

  // Add metadata
  const mergedMetadata = { ...(metadata || {}) };
  if (!mergedMetadata.organization_id && userContext.organizationId) {
    mergedMetadata.organization_id = userContext.organizationId;
  }
  const resolvedBrandId = brandId || userContext.brandId;
  if (!mergedMetadata.brand_id && resolvedBrandId) {
    mergedMetadata.brand_id = resolvedBrandId;
  }
  if (Object.keys(mergedMetadata).length > 0) {
    payload.metadata = mergedMetadata;
  }

  // Add mode if provided (ask, workflow, agents)
  if (mode) {
    payload.mode = mode;
  }

  // Add agent_id if provided (routes directly to that agent)
  if (metadata?.routed_agent) {
    payload.agent_id = metadata.routed_agent;
  }

  // Add confirmation response if provided
  if (confirmationId) {
    payload.confirmation_id = confirmationId;
    payload.confirmation_response = confirmationResponse;
  }

  // Add file attachments (text content already extracted on frontend)
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments;
    console.log("[Chat] Sending attachments:", attachments.map(a => ({ name: a.name, hasContent: !!a.content, contentLen: a.content?.length })));
  }

  const options = {
    method: "POST",
    body: JSON.stringify(payload),
  };

  return await apiRequest(url, options);
};

// =============================================================================
// Response Helpers
// =============================================================================

/**
 * Check if response is awaiting HITL confirmation
 * @param {Object} response - Chat response
 * @returns {boolean}
 */
export const isAwaitingConfirmation = (response) => {
  return (
    response?.status === "awaiting_confirmation" ||
    response?._httpStatus === 202
  );
};

/**
 * Check if response is completed
 * @param {Object} response - Chat response
 * @returns {boolean}
 */
export const isCompleted = (response) => {
  return response?.status === "completed";
};

/**
 * Check if response has error
 * @param {Object} response - Chat response
 * @returns {boolean}
 */
export const hasError = (response) => {
  return response?.status === "error";
};

/**
 * Transform MAARAN blocks to frontend format
 *
 * MAARAN format: { kind: "markdown"|"component"|"suggestions", ... }
 * Frontend format: { kind: "markdown"|"ui_component", ... }
 *
 * @param {Array} blocks - MAARAN blocks
 * @returns {Array} Frontend-compatible blocks
 */
/**
 * Strip wrapping triple-backtick code fences from markdown text.
 * AI models sometimes wrap their entire markdown response in ```markdown ... ```
 * which causes the frontend to render it as a code block instead of formatted markdown.
 */
const stripWrappingCodeFence = (text) => {
  if (!text || typeof text !== "string") return text;
  const trimmed = text.trim();
  // Match: starts with ```<optional language tag>\n and ends with ```
  const match = trimmed.match(/^```(?:\w+)?\s*\n([\s\S]*?)\n\s*```\s*$/);
  if (match) return match[1];
  return text;
};

export const transformBlocksToFrontend = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) {
    return [];
  }

  return blocks.map((block) => {
    switch (block.kind) {
      case "markdown":
        return {
          kind: "markdown",
          text: stripWrappingCodeFence(block.text),
        };

      case "component":
        return {
          kind: "ui_component",
          name: block.name,
          props: block.props || {},
          actions: [],
        };

      case "suggestions":
        return {
          kind: "suggestions",
          items: block.items || [],
        };

      default:
        // Pass through unknown blocks
        return block;
    }
  });
};

/**
 * Build assistant message from MAARAN response
 * @param {Object} response - MAARAN chat response
 * @returns {Object} Frontend message object
 */
export const buildAssistantMessage = (response) => {
  const blocks = transformBlocksToFrontend(response.blocks);

  // If no blocks but has response text, create a markdown block
  if (blocks.length === 0 && response.response) {
    blocks.push({
      kind: "markdown",
      text: stripWrappingCodeFence(response.response),
    });
  }

  return {
    id: crypto.randomUUID(),
    role: "assistant",
    content: response.response || "",
    blocks,
    agentName: response.agent_name,
    actions: [],
    suggestions: [],
    // Include confirmation info if present (for HITL)
    confirmation: response.confirmation || null,
  };
};

// =============================================================================
// Confirmations API
// =============================================================================

/**
 * Get pending confirmations for a session
 * @param {string} sessionId - Session UUID
 * @returns {Promise<Object>} List of pending confirmations
 */
export const getPendingConfirmations = async (sessionId) => {
  const url = `${BASE_URL}${API_PREFIX}/confirmations/pending?session_id=${sessionId}`;
  return await apiRequest(url, { method: "GET" });
};

/**
 * Resolve a confirmation (approve or reject)
 * @param {string} confirmationId - Confirmation UUID
 * @param {string} action - "approved" or "rejected"
 * @param {string} [note] - Optional resolution note
 * @returns {Promise<Object>} Resolution result
 */
export const resolveConfirmation = async (confirmationId, action, note) => {
  const url = `${BASE_URL}${API_PREFIX}/confirmations/${confirmationId}/resolve`;
  const options = {
    method: "POST",
    body: JSON.stringify({
      action,
      note: note || undefined,
    }),
  };

  return await apiRequest(url, options);
};

// =============================================================================
// Agents API
// =============================================================================

/**
 * Get list of available agents
 * @returns {Promise<Object>} List of agents with their capabilities
 */
export const getAgentRegistry = async () => {
  const url = `${BASE_URL}${API_PREFIX}/agents/registry`;
  return await apiRequest(url, { method: "GET" });
};

// =============================================================================
// Lookup API
// =============================================================================

/**
 * Lookup entities for chat selection
 * @param {Object} params
 * @param {string} params.type - Entity type (campaigns, tasks, templates, members, phases)
 * @param {string} [params.query] - Search query
 * @param {number} [params.limit=10] - Max results
 * @param {string|number} [params.campaignId] - Campaign ID for task/phase lookup
 * @param {string|number} [params.brandId] - Brand ID for filtering
 * @param {string} [params.phase] - Phase name filter
 * @returns {Promise<Object>} Lookup result
 */
export const searchEntities = async ({
  type,
  query,
  limit = 10,
  campaignId,
  brandId,
  phase,
} = {}) => {
  if (!type) {
    throw new Error("type is required");
  }

  const params = new URLSearchParams({ type });
  if (query) params.append("query", query);
  if (limit) params.append("limit", limit.toString());
  if (campaignId) params.append("campaign_id", String(campaignId));
  if (brandId) params.append("brand_id", String(brandId));
  if (phase) params.append("phase", phase);

  const url = `${BASE_URL}${API_PREFIX}/lookup?${params.toString()}`;
  return await apiRequest(url, { method: "GET" });
};

// =============================================================================
// Health Check
// =============================================================================

/**
 * Check if MAARAN API is healthy
 * @returns {Promise<Object>} Health status
 */
export const healthCheck = async () => {
  const url = `${BASE_URL}/health`;
  return await apiRequest(url, { method: "GET" });
};

export const getWorkflow = async (workflowId) => {
  const url = `${BASE_URL}${API_PREFIX}/workflows/${workflowId}`;
  return await apiRequest(url, { method: "GET" });
};

export const getWorkflowByCampaign = async (campaignId) => {
  if (!campaignId) {
    throw new Error("campaignId is required");
  }
  const encodedCampaignId = encodeURIComponent(String(campaignId));
  const url = `${BASE_URL}${API_PREFIX}/workflows/by-campaign/${encodedCampaignId}`;
  return await apiRequest(url, { method: "GET" });
};

export const joinCampaignWorkflow = async (campaignId) => {
  if (!campaignId) {
    throw new Error("campaignId is required");
  }
  const encodedCampaignId = encodeURIComponent(String(campaignId));
  const url = `${BASE_URL}${API_PREFIX}/workflows/by-campaign/${encodedCampaignId}/join`;
  return await apiRequest(url, { method: "POST" });
};

export const createWorkflowFromCampaign = async (campaignId) => {
  if (!campaignId) {
    throw new Error("campaignId is required");
  }
  const encodedCampaignId = encodeURIComponent(String(campaignId));
  const url = `${BASE_URL}${API_PREFIX}/workflows/by-campaign/${encodedCampaignId}/create`;
  return await apiRequest(url, { method: "POST" });
};

export default {
  // Session
  createSession,
  getSession,
  listSessions,
  closeSession,
  deleteSession,
  // Chat
  sendChatMessage,
  // Confirmations
  getPendingConfirmations,
  resolveConfirmation,
  // Agents
  getAgentRegistry,
  // Lookup
  searchEntities,
  // Workflows
  getWorkflow,
  getWorkflowByCampaign,
  joinCampaignWorkflow,
  createWorkflowFromCampaign,
  // Health
  healthCheck,
  // Helpers
  isAwaitingConfirmation,
  isCompleted,
  hasError,
  transformBlocksToFrontend,
  buildAssistantMessage,
  // Auth
  clearUserContextCache,
};
